/**
 * The Documents tab — the document state token (VALE-404).
 *
 * A thin mapping onto the standard `StatusBadge`; it writes no colour of its
 * own. A `ready` document renders nothing at all: a list where every row carries
 * a chip reads as noise, and "fine" is the default the eye should skip over.
 */

'use client';

import { StatusBadge } from '@/components/ui/status-badge';
import type {
  StatusBadgeEmphasis,
  StatusBadgeTone
} from '@/components/ui/status-badge';
import type { DocState } from './labels';

const STATE_TOKEN: Record<
  DocState,
  { label: string; tone: StatusBadgeTone } | null
> = {
  ready: null,
  // `info` rather than the `ai` tone: purple is the loudest rung on the ladder,
  // and being read is the most transient state on the surface — it should not
  // out-shout a document that is genuinely stuck.
  analysing: { label: 'Reading', tone: 'info' },
  stalled: { label: 'Stalled', tone: 'caution' }
};

export function DocStateBadge({
  state,
  emphasis = 'subtle'
}: {
  state: DocState;
  emphasis?: StatusBadgeEmphasis;
}) {
  const token = STATE_TOKEN[state];
  if (!token) return null;
  return (
    <StatusBadge tone={token.tone} emphasis={emphasis} className="shrink-0">
      {token.label}
    </StatusBadge>
  );
}
